import { LocalizationString } from '@celito.clients/assets';
import { ButtonTypes, FormEngineModeTypeEnum } from '@celito.clients/enums';
import { FormTypeRenderer } from '@celito.clients/form-engine';
import { useLayout } from '@celito.clients/hooks';
import { UserContext } from '@celito.clients/provider';
import {
  CustomButton,
  GridView,
  GridViewProps,
  HeaderActionButtonsContainer,
  Icon,
} from '@celito.clients/shared';
import { useTheme } from '@celito.clients/theme';
import { Role } from '@celito.clients/types';
import { Skeleton, SkeletonItem } from '@fluentui/react-components';
import { RulesComponentListViewFilter } from 'libs/shared/src/lib/rules-component/types/rules-component.types';
import { useContext } from 'react';
import ReactPaginate from 'react-paginate';

import { ListViewActions } from './components';
import { DelegationModal } from './components/delegation-modal/delegation-modal.component';
import CreateWorkflow from './components/workflow-modal';
import { ListViewProps } from './list-view.model';
import classes from './list-view.module.css';
import { ErrorData, ExportItem, ObjectMetadata, ViewMetadata } from './types';
import { transformObjectMetadata } from './utils';

export interface ListViewUiProps extends ListViewProps {
  isLoading: boolean;
  columnData: GridViewProps['columns'];
  rowData: GridViewProps['items'];
  extraGridProps: Omit<GridViewProps, 'columns' | 'items'>;
  error: ErrorData;
  handlePageChange: (page: number) => void;
  itemsPerRow: number;
  totalItems: number;
  createRoute?: string | null;
  applyFilters: (
    rawNewFilters: RulesComponentListViewFilter,
    doApiCall?: boolean
  ) => void;
  viewMetadata: ViewMetadata | null;
  numberOfFilters: number;
  currentPageNumber: number;
  objectName: string;
  exportFile: (exportItem: ExportItem) => void;
  onCancelPress: () => void;
  objectMetadata: ObjectMetadata | null;
  createRecordViewType?: string;
  handleWorkflowModalOpen: () => void;
  openWorkFlowModal: boolean;
  parentRecordRoles?: string[];
  parentRecordLabel?: string;
  parentRecordName?: string;
}

const ListViewUi = ({
  isLoading,
  columnData,
  rowData,
  dataTestId,
  extraGridProps,
  error,
  handlePageChange,
  itemsPerRow,
  totalItems,
  createRoute,
  applyFilters,
  viewMetadata,
  numberOfFilters,
  currentPageNumber,
  objectName,
  exportFile,
  onCancelPress,
  objectMetadata,
  createRecordViewType,
  handleWorkflowModalOpen,
  openWorkFlowModal,
  leftActionNode,
  showAddButton = true,
  parentRecordRoles = [],
  parentObjectName,
  parentRecordLabel,
  parentRecordName,
}: ListViewUiProps): JSX.Element => {
  const { cssVariables } = useTheme();
  const { domainObjects, isBizAdmin } = useContext(UserContext);
  const { pageTitle } = useLayout();
  const viewData = viewMetadata?.viewDto?.view?.[0];
  const pageCount = Math.ceil(totalItems / itemsPerRow);

  const getPageRange = () => {
    if (currentPageNumber === 1) {
      return itemsPerRow + 1;
    }
    if (pageCount - currentPageNumber <= 5) {
      return itemsPerRow + 1;
    }
    if (itemsPerRow - currentPageNumber <= 3) {
      return itemsPerRow - 1;
    }

    return itemsPerRow;
  };

  const isActionAllowedWithRecordRoles = (
    parentRecordRoles: string[],
    mode?: FormEngineModeTypeEnum
  ): boolean => {
    if (mode && mode === FormEngineModeTypeEnum.View) {
      return true;
    }
    if (!viewMetadata?.viewDto?.parentRecordRoles) {
      return true;
    }

    // Check if user has parent record role
    const hasParentRecordRole = parentRecordRoles.some((role) =>
      viewMetadata?.viewDto?.parentRecordRoles?.includes(role)
    );

    // If user has parent record role or user roles are in parent record roles, allow action
    return hasParentRecordRole;
  };

  // Check if user has create or update permissions for the current object
  const hasPermissionToInitiate = () =>
    !!domainObjects?.find((obj) => obj.name === objectName)
      ?.hasCreatePermission;

  return (
    <div data-testid={dataTestId}>
      {isLoading ? (
        <Skeleton animation="pulse">
          <SkeletonItem style={{ marginTop: '10px', height: '640px' }} />
          {/* Call the delegation modal initially to load the cached users response */}
          <DelegationModal
            isOpen={false}
            onClose={() => {}}
            rowItem={{ key: 'test' }}
            objectName={objectName}
          />
        </Skeleton>
      ) : (
        <>
          {/* Create Button */}
          {(createRecordViewType ||
            viewData?.showCancelButton ||
            (createRoute &&
              isActionAllowedWithRecordRoles(parentRecordRoles))) && (
            <HeaderActionButtonsContainer>
              <div className={classes.listViewActionButtons}>
                {createRecordViewType && (
                  <CustomButton
                    buttonTitle={LocalizationString.ADD_NEW}
                    buttonType={ButtonTypes.Transparent}
                    leftIcon="Add"
                    onClick={handleWorkflowModalOpen}
                  />
                )}
                {viewData?.showCancelButton && (
                  <CustomButton
                    buttonTitle={LocalizationString.CANCEL}
                    buttonType={ButtonTypes.Transparent}
                    onClick={onCancelPress}
                  />
                )}
                {createRoute &&
                  showAddButton &&
                  (isBizAdmin
                    ? hasPermissionToInitiate()
                    : isActionAllowedWithRecordRoles(parentRecordRoles) &&
                      hasPermissionToInitiate()) && (
                    <FormTypeRenderer
                      parentObjectName={parentObjectName}
                      parentRecordLabel={parentRecordLabel}
                      parentRecordName={parentRecordName}
                      viewData={viewData}
                      route={`../${createRoute}`}
                      buttonTitle={
                        viewData?.createRecordButtonTitle ??
                        LocalizationString.ADD_NEW
                      }
                      buttonType={ButtonTypes.Transparent}
                    />
                  )}
              </div>
            </HeaderActionButtonsContainer>
          )}
          <div className={classes.container}>
            {pageTitle === undefined && (
              <div className={classes.emptyTitleSpace} />
            )}
            {/* ListView Actions  */}
            {viewMetadata && (
              <ListViewActions
                columnData={columnData}
                applyFilters={applyFilters}
                viewMetadata={viewMetadata}
                numberOfFilters={numberOfFilters}
                objectName={objectName}
                hasData={rowData?.length > 0}
                objectMetadata={transformObjectMetadata(objectMetadata!)}
                exportFile={exportFile}
                leftActionNode={leftActionNode}
              >
                {createRoute &&
                  !showAddButton &&
                  !error.isError &&
                  (isBizAdmin
                    ? hasPermissionToInitiate()
                    : isActionAllowedWithRecordRoles(parentRecordRoles) &&
                      hasPermissionToInitiate()) && (
                    <FormTypeRenderer
                      parentObjectName={parentObjectName}
                      parentRecordLabel={parentRecordLabel}
                      parentRecordName={parentRecordName}
                      viewData={viewData}
                      route={`../${createRoute}`}
                      buttonTitle={
                        viewData?.createRecordButtonTitle ??
                        LocalizationString.ADD_NEW
                      }
                    />
                  )}
              </ListViewActions>
            )}

            {/* Grid view */}
            <GridView
              className={classes.listGridView}
              columns={columnData}
              items={error.isError ? [] : rowData}
              {...extraGridProps}
            />

            {/* Pagination */}
            {pageCount > 1 && (
              <div className={classes.bottomContainer}>
                <div
                  data-testid="pagination-list-view"
                  className={classes.pagination}
                  style={{ ...cssVariables }}
                >
                  <ReactPaginate
                    breakLabel="..."
                    nextLabel={
                      <Icon
                        data-testid="icon-pagination-right"
                        iconName="ChevronRight16Regular"
                      />
                    }
                    previousLabel={
                      <Icon
                        data-testid="icon-pagination-left"
                        iconName="ChevronLeft16Regular"
                      />
                    }
                    forcePage={currentPageNumber - 1}
                    onPageChange={({ selected }) => handlePageChange(selected)}
                    // pageRangeDisplayed={itemsPerRow}
                    pageCount={pageCount}
                    activeClassName={classes.active}
                    disabledClassName={classes.disabled}
                    marginPagesDisplayed={1}
                    pageRangeDisplayed={getPageRange()}
                  />
                </div>
              </div>
            )}
          </div>
        </>
      )}
      {openWorkFlowModal && (
        <CreateWorkflow
          openWorkFlowModal={openWorkFlowModal}
          handleWorkflowModalOpen={handleWorkflowModalOpen}
        />
      )}
    </div>
  );
};

export default ListViewUi;
